import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate } from "react-router-dom";
import ModelIndex, { ListActionProps, ModelIndexHandle } from "@components/layout/ModelIndex";
import { faBan, faCheck, faCopy, faEdit, faEnvelope, faEye, faFile, faPrint } from "@fortawesome/free-solid-svg-icons";
import { formatMoneyValue } from "@utils/Currency";
import styles from "./style.module.scss";
import moment from "moment";
import { QuoteApi, QuoteDataType } from "@/api/QuoteApi";
import { OpenSendDocumentPubSub } from "@utils/pubsubs/PubSub";
import SendDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendDocumentToEmailOffCanvas";
import { useAppSelector } from "@/store/hooks";
import { showAlert } from "@utils/Alert";
import useRoleAccess from "@/hooks/useRoleAccess";
import {
  QUOTE_APPROVAL_APPROVED,
  QUOTE_APPROVAL_BLOCKED,
  QUOTE_APPROVAL_BLOCK_MESSAGE,
  QUOTE_APPROVAL_COLORS,
  QUOTE_APPROVAL_LABEL,
  QuoteApprovalStatus,
  resolveQuoteApprovalStatus,
} from "@utils/quoteApprovalStatus";

const QuoteIndex = () => {
  const navigate = useNavigate();
  const modelRef = useRef<ModelIndexHandle>(null);
  const suscriptionData = useAppSelector(state => state.suscription.suscription)
  const approvalFlowActive = suscriptionData?.quote_approval_flow || false
  const { canDo } = useRoleAccess();
  // El listado se sirve de Meili, que tarda en reflejar el update: se mantiene el
  // estado recien guardado en memoria para que la fila cambie de inmediato.
  const [statusOverrides, setStatusOverrides] = useState<Record<string, QuoteApprovalStatus>>({});

  useEffect(() => {
  }, []);

  const currentStatus = (item: QuoteDataType): QuoteApprovalStatus => {
    const override = item.id ? statusOverrides[item.id] : undefined;
    return override ?? resolveQuoteApprovalStatus(item.approval_status);
  }

  const changeApprovalStatus = async (id: string, status: QuoteApprovalStatus) => {
    try {
      await QuoteApi.update(id, { approval_status: status });
      setStatusOverrides(prev => ({ ...prev, [id]: status }));
      showAlert("success", `La Cotizacion ha sido ${status === QUOTE_APPROVAL_APPROVED ? "aprobada" : "bloqueada"}`);
    } catch (err) {
      showAlert("danger", "No fue posible actualizar el estado de la Cotizacion");
    }
  }

  const goToInvoiceFromQuote = (id: string, item: QuoteDataType) => {
    if (approvalFlowActive) {
      const status = currentStatus(item);
      if (status !== QUOTE_APPROVAL_APPROVED) {
        showAlert("danger", QUOTE_APPROVAL_BLOCK_MESSAGE[status]);
        return;
      }
    }
    navigate(`/user/invoices/fromQuote/${id}`);
  }

  const approvalActions: ListActionProps<QuoteDataType>[] = !approvalFlowActive ? [] : [
    ...(canDo("action:quote:approve") ? [{
      actionTitle: "Aprobar Cotizacion",
      iconProps: { icon: faCheck, className: styles.approveBtn },
      isVisible: (item: QuoteDataType) => currentStatus(item) !== QUOTE_APPROVAL_APPROVED,
      needConfirm: true,
      confirmProps: {
        title: "Aprobar Cotizacion",
        confirmMessage: "¿Seguro que desea aprobar esta Cotizacion?",
        confirmBtnText: "Aprobar"
      },
      onClick: (id: string) => { changeApprovalStatus(id, QUOTE_APPROVAL_APPROVED) }
    }] : []),
    ...(canDo("action:quote:block") ? [{
      actionTitle: "Bloquear Cotizacion",
      iconProps: { icon: faBan, className: styles.blockBtn },
      isVisible: (item: QuoteDataType) => currentStatus(item) !== QUOTE_APPROVAL_BLOCKED,
      needConfirm: true,
      confirmProps: {
        title: "Bloquear Cotizacion",
        confirmMessage: "¿Seguro que desea bloquear esta Cotizacion?",
        confirmBtnText: "Bloquear"
      },
      onClick: (id: string) => { changeApprovalStatus(id, QUOTE_APPROVAL_BLOCKED) }
    }] : []),
  ];

  const triggerEmailSend = (item: QuoteDataType) => {
    OpenSendDocumentPubSub.publish({
      openOffCanvas: true,
      emails: item.email || "",
      sourceURL: `/user/quotes/print/${item.id}/0/1`,
      emailProps: {
        emailBody: suscriptionData?.qoute_email || "",
        emailFilename: `Cotizacion #${item.numero}`,
        emailSubject: `Cotizacion #${item.numero}`,
        Suscription: {
          email: suscriptionData?.email || ""
        }
      }
    })
  }

  return (
    <PageContainer
      title="Lista Cotizaciones"
      backUrl="/user/dashboard"
      primaryActions={[
        {
          onClick: () => {
            navigate('/user/quotes/add')
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
    >
      <ModelIndex<QuoteDataType>
        ref={modelRef}
        meiliConfig={{
          index: "quotes",
          sort: ["numero:desc"]
        }}
        messages={{
          noResults: "No hay cotizaciones creadas aun"
        }}
        fetchData={(...args) => QuoteApi.fetchAll(...args)}
        filterProps={[
          {
            key: "nombre_factura",
            filterType: "contains"
          },
          {
            key: "numero",
            filterType: "eq",
            type: "number"
          }
        ]}
        primaryProperties={[
          {
            key: "numero",
            highlighted: true,
            className: styles.sequence
          },
          {
            key: (d) => d.nombre_factura || d.client?.nombre || "",
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "2", xs: "12" },
            { sm: "10", xs: "12" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              const date = moment(data.fecha, "YYYY-MM-DD")
              return date.format("DD-MM-YYYY")
            },
            title: "Fecha",
          },
          // El telefono cede su lugar al estado: con el flujo de aprobacion activo importa
          // mas que la fila quepa en una sola linea.
          ...(approvalFlowActive ? [] : [{
            key: "tel1" as keyof QuoteDataType,
            title: "Telefono"
          }]),
          ...(approvalFlowActive ? [{
            key: (data: QuoteDataType) => QUOTE_APPROVAL_LABEL[currentStatus(data)],
            title: "Estado",
            highlighted: true,
            optionalHighlighted: (_val: unknown, data: QuoteDataType) => currentStatus(data),
            highlightedColors: QUOTE_APPROVAL_COLORS
          }] : []),
          {
            key: (data) => {
              if ("currency_id" in data) {
                return formatMoneyValue(data.total, (data as any).currency_id)
              }
              // La fila puede venir sin la moneda resuelta (el indice no la trae y el
              // modelo no la relaciona): sin el encadenamiento opcional el listado entero
              // revienta. `formatMoneyValue` ya devuelve "" cuando no hay moneda.
              return formatMoneyValue(data.total, data.currency?.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          approvalFlowActive
            ? [
              { sm: "4", md: "2" },
              { sm: "4", md: "2" },
              { sm: "4", md: "3" }
            ]
            : [
              { sm: "4", md: "2" },
              { sm: "4", md: "3" },
              { sm: "4", md: "2" }
            ]
        }
        includeCustomFields={true}
        customFieldsDocumentType={"quote"}
        listActions={[
          {
            href: "/user/quotes/copy/{:id}",
            actionTitle: "Copiar de Cotizaciones",
            iconProps: { icon: faCopy }
          },
          ...approvalActions,
          {
            href: "/user/invoices/fromQuote/{:id}",
            actionTitle: "Crear Factura con base a esta Cotizaciones",
            iconProps: { icon: faFile },
            onClick: (id, item) => goToInvoiceFromQuote(id, item)
          },
          {
            href: "/user/quotes/edit/{:id}",
            actionTitle: "Editar Cotizacion",
            iconProps: { icon: faEdit }
          },
          {
            onClick: (id, item) => triggerEmailSend(item),
            actionTitle: "Enviar Por Correo ",
            iconProps: { icon: faEnvelope }
          },
          {
            href: "/user/quotes/view/{:id}",
            actionTitle: "Ver Cotizacion",
            target: "_blank",
            iconProps: { icon: faEye }
          },
          {
            href: "/user/quotes/print/{:id}/0/1",
            actionTitle: "Imprimir Cotizacion",
            target: "_blank",
            iconProps: { icon: faPrint }
          }
        ]}
      >
      </ModelIndex>

      <SendDocumentToEmailOffCanvas
        title="Envio de Cotizacion a Email" />

    </PageContainer >
  );
}

export default QuoteIndex;
